/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChartPie,
  HiOutlineCollection,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineOfficeBuilding,
  HiOutlinePencil,
  HiOutlineStar,
} from "react-icons/hi";

import ListPage from "../../../layout/ListPage";
import { Badge } from "../../../ui";
import type { Action, Column } from "../../../ui";
import { _PAGE_SIZE_ } from "../../../lib/other/tools";
import { useAdminContext } from "../../../context/admin/AdminContext";
import { useCityContext } from "../../../context/levels";
import { addOneLevel } from "../../../lib/breadCrumb/BreadCrumb";
import {
  pushCityDashboard,
  pushEditLevel,
  pushLevel,
} from "../../../lib/sysNavigation/SysNavigation";
// @ts-ignore — JS helper.
import { getCustomCity, getLevelIds } from "./helper";

type CityRow = {
  name?: string;
  country_name?: string;
  country_id?: number | string;
  level?: string;
  priority?: number;
  status?: number;
  first_level_id?: number | string;
  second_level_id?: number | string;
  third_level_id?: number | string;
  fourth_level_id?: number | string;
  [key: string]: unknown;
};

const editRoute = "/level/edit";
const contentRoute = "/dosee/data";
const dashboardRoute = "/city/dashboard";

const rowKeyOf = (row: CityRow) => {
  const level = String(row.level ?? "");
  const id =
    level === "First"
      ? row.first_level_id
      : level === "Second"
      ? row.second_level_id
      : level === "Third"
      ? row.third_level_id
      : row.fourth_level_id;
  return `${row.name}-${id ?? ""}`;
};

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = (sysAdmin as any)?.role === "super_admin";

  const { cities, count, fetchCities, addPriority, setCities, updateCityStatus } =
    useCityContext() as any;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!cities) fetchCities(page, pageSize);
  }, [cities]);

  const rows = useMemo<CityRow[] | null>(() => {
    if (!Array.isArray(cities)) return null;

    // Ordering rules (top → bottom):
    //   1. Active cities (status === 1) before hidden ones.
    //   2. Within each status bucket, prioritized (priority === 1) first.
    //   3. Then alphabetical by name.
    const byName = (a: CityRow, b: CityRow) =>
      (a?.name ?? "").localeCompare(b?.name ?? "");
    const rank = (c: CityRow) => {
      const activeRank = c?.status === 1 ? 0 : 1;
      const priorityRank = c?.priority === 1 ? 0 : 1;
      return activeRank * 2 + priorityRank;
    };
    const base = [...(cities as CityRow[])].sort((a, b) => {
      const diff = rank(a) - rank(b);
      return diff !== 0 ? diff : byName(a, b);
    });

    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((c) =>
      [c.name, c.country_name, c.level].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      ),
    );
  }, [cities, query]);

  const levelKeyFor = (level?: string) =>
    level === "First"
      ? "first_level_id"
      : level === "Second"
      ? "second_level_id"
      : level === "Third"
      ? "third_level_id"
      : "fourth_level_id";

  const togglePriority = async (row: CityRow) => {
    const { level_id } = getLevelIds({ city: row });
    const key = levelKeyFor(row.level);
    const next = row.priority === 0 ? 1 : 0;
    const { status } = await addPriority({ key, id: level_id, priority: next });
    if (status === 200 && Array.isArray(cities)) {
      const updated = (cities as CityRow[]).map((c) =>
        (c as any)[key] === level_id ? { ...c, priority: next } : c,
      );
      setCities(updated);
    }
  };

  const toggleStatus = async (row: CityRow) => {
    const { level_id } = getLevelIds({ city: row });
    const next = row.status === 1 ? 0 : 1;
    const res = await updateCityStatus({
      level: row.level,
      id: level_id,
      currentStatus: row.status,
    });
    if (res?.status === 200 && Array.isArray(cities)) {
      const key = levelKeyFor(row.level);
      const updated = (cities as CityRow[]).map((c) =>
        (c as any)[key] === level_id ? { ...c, status: next } : c,
      );
      setCities(updated);
    }
  };

  const openEdit = (row: CityRow) => {
    pushEditLevel({
      data: row,
      level: row.level,
      country_id: row.country_id,
    });
    navigate(editRoute);
  };

  const openDashboard = (row: CityRow) => {
    const ids = getLevelIds({ city: row });
    const customCity = getCustomCity({
      city: row,
      first_level_id: ids.first_level_id,
      third_level_id: ids.third_level_id,
      second_level_id: ids.second_level_id,
      fourth_level_id: ids.fourth_level_id,
    });
    pushCityDashboard([row.level, customCity]);
    navigate(dashboardRoute);
  };

  const openContent = (row: CityRow) => {
    const ids = getLevelIds({ city: row });
    const customCity = getCustomCity({
      city: row,
      first_level_id: ids.first_level_id,
      third_level_id: ids.third_level_id,
      second_level_id: ids.second_level_id,
      fourth_level_id: ids.fourth_level_id,
    });
    const lvlLower = String(row.level ?? "").toLowerCase();
    addOneLevel({
      id: ids.level_id,
      name: row.name,
      route: `/levels/${lvlLower}`,
      level: row.level,
      data: { data: customCity },
    });
    pushLevel({
      level: {
        id: ids.level_id,
        name: row.name,
        level: row.level,
        data: customCity,
        route: `/levels/${lvlLower}`,
      },
    });
    navigate(contentRoute);
  };

  const columns: Column<CityRow>[] = [
    {
      header: "City",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineOfficeBuilding className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">
              {row.name ?? "—"}
            </p>
            <p className="truncate text-[11px] text-gray-500">
              {row.country_name ?? ""}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: "Level",
      cell: (row) => <Badge tone="neutral">{row.level ?? "—"}</Badge>,
    },
    {
      header: "Priority",
      cell: (row) =>
        row.priority === 1 ? (
          <Badge tone="brand">On top</Badge>
        ) : (
          <Badge tone="neutral">Standard</Badge>
        ),
    },
    {
      header: "Status",
      cell: (row) =>
        row.status === 1 ? (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
            Hidden
          </span>
        ),
    },
  ];

  const rowActions = (row: CityRow): Action[] => {
    const actions: Action[] = [];

    actions.push({
      label: "Dashboard",
      icon: <HiOutlineChartPie className="h-3.5 w-3.5" />,
      onClick: () => openDashboard(row),
    });

    actions.push({
      label: "Content",
      icon: <HiOutlineCollection className="h-3.5 w-3.5" />,
      onClick: () => openContent(row),
    });

    if (isSuperAdmin) {
      actions.push({
        label: row.priority ? "Remove top" : "Push to top",
        icon: <HiOutlineStar className="h-3.5 w-3.5" />,
        onClick: () => togglePriority(row),
        confirm: {
          title: row.priority ? "Remove priority?" : "Prioritise this city?",
          description: row.priority
            ? "This city will no longer appear at the top of lists."
            : "This city will be surfaced at the top where applicable.",
          confirmLabel: "Proceed",
        },
      });

      actions.push({
        label: "Edit",
        icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
        onClick: () => openEdit(row),
      });
    }

    actions.push({
      label: row.status === 1 ? "Hide" : "Show",
      icon:
        row.status === 1 ? (
          <HiOutlineEyeOff className="h-3.5 w-3.5" />
        ) : (
          <HiOutlineEye className="h-3.5 w-3.5" />
        ),
      onClick: () => toggleStatus(row),
      confirm: {
        title:
          row.status === 1
            ? `Hide ${row.name ?? "this city"}?`
            : `Make ${row.name ?? "this city"} active?`,
        description:
          row.status === 1
            ? "This city will no longer appear on the public website."
            : "This city will appear on the public website.",
        confirmLabel: row.status === 1 ? "Hide" : "Activate",
      },
    });

    return actions;
  };

  return (
    <ListPage<CityRow>
      title="Cities"
      subtitle="Cities listed under each administrative level."
      icon={<HiOutlineOfficeBuilding className="h-5 w-5" />}
      rows={rows}
      columns={columns}
      rowKey={(r) => rowKeyOf(r)}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search by city, country, or level…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchCities(p, ps);
        },
      }}
      empty="No cities yet."
    />
  );
};

export default Overview;
